import React from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, LogOut } from "lucide-react";

/**
 * Shown at the very top of every page while a platform admin is "inside" a
 * tenant workspace via impersonation. One click to exit and go back to the
 * platform admin session.
 */
export default function ImpersonationBanner() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  if (!user?.impersonating) return null;

  const exit = async () => {
    try {
      await api.post("/auth/exit-impersonation");
      const me = await refresh();
      navigate(me?.role === "platform_admin" ? "/platform/companies" : "/login", {
        replace: true,
      });
    } catch {
      // silent — user can still log out manually
    }
  };

  return (
    <div
      className="bg-amber-construction text-graphite border-b-2 border-graphite"
      data-testid="impersonation-banner"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-2 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle size={14} className="flex-shrink-0" />
          <span className="font-bold truncate">
            Viewing as{" "}
            <span className="font-mono">{user.email}</span>
            {user.company?.name ? ` · ${user.company.name}` : ""}
          </span>
          <span className="hidden sm:inline text-graphite/70 font-mono text-xs">
            (platform admin: {user.impersonator?.email})
          </span>
        </div>
        <button
          onClick={exit}
          data-testid="exit-impersonation-btn"
          className="flex-shrink-0 inline-flex items-center gap-1.5 bg-graphite text-white hover:bg-black px-3 py-1 text-xs font-bold uppercase tracking-wider transition-colors"
        >
          <LogOut size={12} /> Exit workspace
        </button>
      </div>
    </div>
  );
}
